/**
 * useVoiceInput — React hook for speech-to-text via Web Speech API
 *
 * Features:
 *  - Browser-native SpeechRecognition (Chrome, Edge, Safari)
 *  - Real-time interim + final transcripts
 *  - Domain-aware auto-correction (domainVocabulary)
 *  - Configurable locale with auto-detection fallback
 *  - Continuous listening until manual stop (ChatGPT-style)
 *  - Auto-restart on recognition end to keep session alive
 *  - Accumulates transcript across multiple recognition segments
 *  - Graceful degradation (isSupported flag)
 *
 * @module hooks/useVoiceInput
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { correctTranscript, BOOST_TERMS } from '../utils/domainVocabulary';
import { detectLocale } from '../config/accentMap';

const SpeechRecognition = typeof window !== 'undefined'
  ? window.SpeechRecognition || window.webkitSpeechRecognition
  : null;

const MAX_RECORDING_MS = 300000;  // hard limit: 5 minutes

export default function useVoiceInput({
  user = null,
  locale: localeProp = null,
  continuous = true,
  onResult = null,
  onError = null,
  onSilenceStop = null,
  silenceTimeoutMs = 0,
} = {}) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const [confidence, setConfidence] = useState(0);
  const [error, setError] = useState(null);
  const [locale, setLocale] = useState(localeProp || detectLocale(user));

  const recognitionRef = useRef(null);
  const maxTimerRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const isStoppingRef = useRef(false);
  // Accumulate full transcript across recognition restarts
  const accumulatedRef = useRef('');
  // Keep onSilenceStop in a ref to avoid stale closure in recognition handlers
  const onSilenceStopRef = useRef(onSilenceStop);
  const silenceTimeoutMsRef = useRef(silenceTimeoutMs);
  useEffect(() => { onSilenceStopRef.current = onSilenceStop; }, [onSilenceStop]);
  useEffect(() => { silenceTimeoutMsRef.current = silenceTimeoutMs; }, [silenceTimeoutMs]);

  const isSupported = !!SpeechRecognition;

  // Update locale when prop changes
  useEffect(() => {
    if (localeProp) setLocale(localeProp);
  }, [localeProp]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isStoppingRef.current = true;
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch (_) {}
      }
      clearTimeout(maxTimerRef.current);
      clearTimeout(silenceTimerRef.current);
    };
  }, []);

  const resetTranscript = useCallback(() => {
    setTranscript('');
    setInterim('');
    setConfidence(0);
    setError(null);
    accumulatedRef.current = '';
  }, []);

  const stopListening = useCallback(() => {
    isStoppingRef.current = true;
    clearTimeout(maxTimerRef.current);
    clearTimeout(silenceTimerRef.current);

    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) {}
      recognitionRef.current = null;
    }
    setIsListening(false);
    setInterim('');
  }, []);

  const _createRecognition = useCallback(() => {
    const recognition = new SpeechRecognition();
    recognition.lang = locale;
    recognition.continuous = continuous;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    // Try to set grammar list with domain boost terms
    try {
      const SpeechGrammarList = window.SpeechGrammarList || window.webkitSpeechGrammarList;
      if (SpeechGrammarList) {
        const grammar = `#JSGF V1.0; grammar lina; public <term> = ${BOOST_TERMS.join(' | ')};`;
        const grammarList = new SpeechGrammarList();
        grammarList.addFromString(grammar, 1);
        recognition.grammars = grammarList;
      }
    } catch (_) {
      // Grammar list not supported — ignore
    }

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
    };

    recognition.onresult = (event) => {
      let finalText = '';
      let interimText = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalText += result[0].transcript;
          setConfidence(result[0].confidence || 0);
        } else {
          interimText += result[0].transcript;
        }
      }

      if (interimText) {
        setInterim(interimText);
      }

      if (finalText) {
        const corrected = correctTranscript(finalText);
        // Accumulate — append to existing transcript
        const separator = accumulatedRef.current ? ' ' : '';
        accumulatedRef.current += separator + corrected;
        setTranscript(accumulatedRef.current);
        setInterim('');
        onResult?.(accumulatedRef.current);
      }

      // Silence detection: reset timer on any speech activity
      if (silenceTimeoutMsRef.current > 0) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = setTimeout(() => {
          if (!isStoppingRef.current) {
            isStoppingRef.current = true;
            clearTimeout(maxTimerRef.current);
            if (recognitionRef.current) {
              try { recognitionRef.current.stop(); } catch (_) {}
              recognitionRef.current = null;
            }
            setIsListening(false);
            setInterim('');
            onSilenceStopRef.current?.(accumulatedRef.current);
          }
        }, silenceTimeoutMsRef.current);
      }
    };

    recognition.onerror = (event) => {
      // 'aborted' and 'no-speech' are not real errors in continuous mode
      if (event.error === 'aborted' || event.error === 'no-speech') {
        return;
      }

      const errorMessages = {
        'not-allowed': 'Microphone access denied. Please allow microphone permissions.',
        'network': 'Network error during speech recognition. Check your connection.',
        'audio-capture': 'No microphone detected. Please connect a microphone.',
        'service-not-allowed': 'Speech recognition service is not available.',
      };

      const msg = errorMessages[event.error] || `Speech recognition error: ${event.error}`;
      setError(msg);
      setIsListening(false);
      onError?.(msg);
    };

    recognition.onend = () => {
      // Auto-restart if not manually stopped (Chrome kills recognition after ~60s)
      if (!isStoppingRef.current) {
        try {
          const newRecognition = _createRecognition();
          recognitionRef.current = newRecognition;
          newRecognition.start();
        } catch (_) {
          setIsListening(false);
        }
        return;
      }
      setIsListening(false);
    };

    return recognition;
  }, [locale, continuous, onResult, onError]);

  const startListening = useCallback(() => {
    if (!isSupported) {
      const msg = 'Speech recognition is not supported in this browser. Try Chrome, Edge, or Safari.';
      setError(msg);
      onError?.(msg);
      return;
    }

    // Reset state
    setError(null);
    setInterim('');
    setTranscript('');
    accumulatedRef.current = '';
    isStoppingRef.current = false;

    const recognition = _createRecognition();
    recognitionRef.current = recognition;

    try {
      recognition.start();
    } catch (e) {
      setError(`Failed to start recognition: ${e.message}`);
      onError?.(e.message);
    }

    // Max recording timer (safety limit)
    maxTimerRef.current = setTimeout(() => {
      stopListening();
    }, MAX_RECORDING_MS);
  }, [isSupported, _createRecognition, onError, stopListening]);

  return {
    isSupported,
    isListening,
    transcript,
    interim,
    confidence,
    error,
    locale,
    startListening,
    stopListening,
    resetTranscript,
    setLocale,
  };
}
